import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, finalize, Subscription, take, tap } from "rxjs";
import { API_HOST, settings } from "environments/environment";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { ApiService } from "./api.service";
import { SettingsService } from "./settings.service";
import { PanelService } from "./panel.service";
import { DialogService } from "./dialog.service";
import { ToastService } from "./toast.service";
import { DeviceDetectorService } from 'ngx-device-detector';
import { SnackbarService } from "./snackbar.service";
import Dexie from "dexie";
import { APIResponse } from "shared/models/api-response";
import { NotificationData } from "shared/models/notification-data";
import { UserSettings } from "shared/models/user-settings.model";
import { DashboardApiService } from "./api/dashboard.api.service";

declare var gtag: any; // Add this line to declare the global gtag function

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {

  private _subscriptions: Subscription[] = []
  private _isLoggedIn$ = new BehaviorSubject<boolean>(false);
  public isLoggedIn$ = this._isLoggedIn$.asObservable();


  public readonly ACCESS_TOKEN = settings.accessTokenKey;
  public readonly SESSION_ID = settings.sessionIdKey;
  public readonly REFRESH_TOKEN = settings.refreshTokenKey;

  private readonly _deviceInfo = this._deviceService.getDeviceInfo()
  private readonly _browser = `${this._deviceInfo.browser} v${this._deviceInfo.browser_version}`;
  private readonly _deviceName = `${this._deviceInfo.os} ${this._deviceService.getDeviceInfo().os_version}`;
  private readonly _platform = this._deviceService.deviceType;

  constructor(
    private _router: Router,
    private _http: HttpClient,
    private _apiService: ApiService,
    private _settingsService: SettingsService,
    private _panelService: PanelService,
    private _dialogService: DialogService,
    private _toastService: ToastService,
    private _deviceService: DeviceDetectorService,
    private _snackbarService: SnackbarService,
    private _dashboardApiService: DashboardApiService
  ) {
    this._isLoggedIn$.next(!!this.accessToken);
  }

  get accessToken() {
    return localStorage.getItem(this.ACCESS_TOKEN);
  }

  get sessionId() {
    return localStorage.getItem(this.SESSION_ID);
  }

  get refreshToken() {
    return localStorage.getItem(this.REFRESH_TOKEN);
  }

  isLoggedIn() {
    return this._isLoggedIn$.value;
  }

  loginOrRegister(action: string, emailAddress: string | any, password: string | any, redirect: boolean = true) {

    const toReturn = new BehaviorSubject<any>(null)
    this._http.post(API_HOST + `auth/${action}`, {
      emailAddress,
      password,
      browser: this._browser,
      deviceName: this._deviceName,
      platform: this._platform
    })
      .pipe(take(1))
      .pipe(finalize(() => {
        toReturn.next(true)
      }))
      .subscribe({
        next: (response: APIResponse) => {

          if (response.success == false && action === 'login') {
            this._snackbarService.showSnackBar("Address email or password is incorrect.", "warning")
            return
          }

          if (response.success == false && action === 'register') {
            this._toastService.warning(response.message)
            return
          }

          if (response.success == true && action === 'login') {

            if (typeof response.data.id == 'undefined') { this.logout({ muted: true }) }


            localStorage.setItem(this.ACCESS_TOKEN, response.data.accessToken);
            localStorage.setItem(this.REFRESH_TOKEN, response.data.refreshToken);
            localStorage.setItem(this.SESSION_ID, response.data.sessionId);

            this._apiService.getUserSettings().pipe(take(1)).subscribe({

              next: async (settingsResponse: APIResponse) => {

                if (settingsResponse.success == true) {
                  await this._settingsService.updateUserSettings(<UserSettings>settingsResponse.data);
                  this._settingsService.startPolling();
                  this._dashboardApiService.getCounters().pipe(take(1)).subscribe()
                  this._isLoggedIn$.next(true);

                  this._toastService.showToast(new NotificationData({
                    title: 'Logged in!',
                    status: 'info',
                    message: 'Hello, we wish you a pleasant day at work!',
                  }))
      
                  if (redirect == true) this._router.navigate(['/account/home']);

                } else {
                  this._snackbarService.showSnackBar("Could not get account settings.", "warning")
                  this.logout({ muted: true });
                  return
                }

              }

            })
           
          }

          else if (response.success == true && action === 'register') {
            this._toastService.showToast(new NotificationData({
              title: 'Success',
              status: 'success',
              message: 'Account created.',
            }))

             // Call the new conversion tracking method
             this.tag_report_conversion();

            this._uet_report_conversion()
            
            this.loginOrRegister('login', emailAddress, password, redirect)
          }
        }
      }
      )

    return toReturn.asObservable()
  }

 private tag_report_conversion() {
    if (typeof gtag === 'function') {
      gtag('event', 'conversion', {
          'send_to': 'AW-17624395765/L_fACJ694acbEPXf-9NB'
      });
    }
    return false;
  }


  getRefreshToken() {
    return this._http.post(API_HOST + 'auth/refresh-token', { refreshToken: this.refreshToken }).pipe(
      tap(
        (response: APIResponse) => {
          localStorage.setItem(this.ACCESS_TOKEN, response.data.accessToken);
          localStorage.setItem(this.REFRESH_TOKEN, response.data.refreshToken);
          localStorage.setItem(this.SESSION_ID, response.data.sessionId);
        }
      )
    );
  }

  changePassword(data) {
    return this._http.post(API_HOST + 'auth/change-password', { currentPassword: data.currentPassword, newPassword: data.newPassword });
  }
  changeEmailAddress(data) {
    return this._http.post(API_HOST + 'auth/change-email-address', { password: data.password, emailAddress: data.emailAddress });
  }
  restoreAccount(data) {
    return this._http.post(API_HOST + 'auth/restore-account', data);
  }
  setNewPassword(data) {
    return this._http.post(API_HOST + 'auth/set-new-password', data);
  }

  logout({ muted = false }) {
    this._settingsService.stopPolling();

    this._http.post(API_HOST + `auth/logout`, {})
      .pipe(
        finalize(() => {
          this._isLoggedIn$.next(false);
          localStorage.removeItem(this.ACCESS_TOKEN)
          localStorage.removeItem('role')
          localStorage.removeItem(this.REFRESH_TOKEN)

          if (muted == false) {
            this._toastService.showToast(new NotificationData({
              title: 'See you',
              status: 'info',
              message: 'You have been successfully logged out of the application.',
            }))
          }

          this._deleteDatabases()

          this._router.navigateByUrl('/login');
          this._panelService.close();
        }))
      .pipe(take(1))
      .subscribe({
        next: (response: APIResponse) => {

        }
      })


  }

  /**
   * Clear local storage and set logged status as logged out
   * That method is not calling any logout action because session is broken and it could 
   * result not expected results such as errors
   */
  clearSession() {
    localStorage.clear()
    this._isLoggedIn$.next(false)
    this._deleteDatabases()
  }

  private async _deleteDatabases() {
    try {

      // Remove databases
      const dbs = await Dexie.getDatabaseNames()
      dbs.forEach(db => {
        const deleteRequest = indexedDB.deleteDatabase(db)

        deleteRequest.onsuccess = () => {
          console.log(`[AuthSignOutComponent]: Deleted application databases: ${db}`)
        }

        deleteRequest.onerror = (error) => {
          console.error(`[AuthSignOutComponent]: Error deleting database ${db}:`, error)
        }
      })

    } catch (ex: any) {
      console.log(`[AuthSignOutComponent]: ${ex.message}`);
    }
  }

  private _pushToDataLayer(eventData: any) {
    try {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push(eventData);
      console.log('Data Layer Event:', eventData);
    } catch(err:any){
			console.log("GTM dataLayer push failed");
			console.log(err.message);
		}
  }

  private _uet_report_conversion() {
    window.uetq = window.uetq || [];
    window.uetq.push('event', '', {"event_category":"signup"});
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe())
  }

}